import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Notification } from '../../api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils';
import { SocketContext } from '../../contexts/socket.context';
import { NotificationChannel, ON_NOTIFICATIONS } from '@sportspredict/shared';
import { ROUTES } from '@/constants';

export default function NotificationPopover({ setNotificationsReady, user, triggerClassName }) {
	const socket = useContext(SocketContext);
	const [notifications, setNotifications] = useState([]);
	const [liveNotifications, setLiveNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const triggerPointerActivatedRef = useRef(false);

	const { email: userEmail, id: userId } = user || {};

	const openRef = useRef(open);

	useEffect(() => {
		openRef.current = open;
	}, [open]);

	const loadNotifications = useCallback(async () => {
		try {
			if (!openRef.current || !userEmail) return;
			setIsLoading(true);

			const all = await Notification.customList();

			setNotifications(all);
			setLiveNotifications([]);
		} catch (error) {
			console.error('Failed load notifications', error);
		} finally {
			setIsLoading(false);
		}
	}, [userEmail]);

	useEffect(() => {
		loadNotifications();
	}, [open, userEmail]);

	const notificationsEventListener = useCallback(
		(data) => {
			(async () => {
				if (!data || !Array.isArray(data.targetUsers) || !data.targetUsers.includes(userEmail)) {
					return;
				}

				if (data.channel === NotificationChannel.AppToast) {
					return;
				}

				if (openRef.current) {
					loadNotifications();
				} else {
					setUnreadCount((prev) => prev + 1);
				}
			})();
		},
		[userEmail, loadNotifications]
	);

	useEffect(() => {
		if (!socket || !userId) return;

		//socket.emit('JOIN_NOTIFICATIONS_ROOM');
		socket.on(ON_NOTIFICATIONS, notificationsEventListener);
		setNotificationsReady(true);

		return () => {
			//socket.emit('LEAVE_NOTIFICATIONS_ROOM');
			socket.off(ON_NOTIFICATIONS, notificationsEventListener);
		};
	}, [socket, userId, notificationsEventListener]);

	const notificationData = useMemo(() => {
		const map = new Map();
		notifications.forEach((n) => map.set(n.id, n));
		liveNotifications.forEach((n) => map.set(n.id, n));
		return Array.from(map.values());
	}, [notifications, liveNotifications]);

	const initUnreadCounter = async () => {
		try {
			const all = await Notification.customList();
			if (!all || !all.length) {
				return;
			}

			const unread = all.filter((n) => !n.is_read?.[user.email]).length;
			setUnreadCount(unread);
		} catch (error) {
			console.error('Failed to init unread counter', error);
		}
	};

	const getTimePassed = (dateString) => {
		if (!dateString) return '';
		const created = new Date(dateString);
		const now = new Date();
		const diffMs = now - created;

		const mins = Math.floor(diffMs / (1000 * 60));
		if (mins < 60) return `${mins}m`;

		const hrs = Math.floor(mins / 60);
		if (hrs < 24) return `${hrs}h`;

		const days = Math.floor(hrs / 24);
		return `${days}d`;
	};

	useEffect(() => {
		if (!user) return;
		initUnreadCounter();
	}, [user]);

	useEffect(() => {
		if (!user?.email) {
			setUnreadCount(0);
			return;
		}
		const unread = notificationData.filter((n) => !n.is_read?.[user.email]).length;
		setUnreadCount(unread);
	}, [notificationData, user?.email]);

	const markAsRead = async (notificationId) => {
		if (!user) return;

		try {
			const notification = await Notification.get(notificationId);

			const isRead = notification.is_read || {};
			isRead[user.email] = true;

			await Notification.update(notificationId, { is_read: isRead });

			setNotifications((prev) =>
				prev.map((n) =>
					n.id === notificationId ? { ...n, is_read: { ...n.is_read, [user.email]: true } } : n
				)
			);

			setUnreadCount((prev) => Math.max(0, prev - 1));
		} catch (error) {
			console.error(error);
			throw new Error('Error marking notification as read');
		}
	};

	const markAllAsRead = async () => {
		if (!user || notifications.length === 0) return;

		try {
			const unreadNotifications = notificationData.filter(
				(n) => !(n.is_read && n.is_read[user.email])
			);

			await Promise.all(
				unreadNotifications.map((notification) => {
					const isRead = notification.is_read || {};
					isRead[user.email] = true;
					return Notification.update(notification.id, { is_read: isRead });
				})
			);

			setNotifications((prev) =>
				prev.map((n) => ({
					...n,
					is_read: { ...n.is_read, [user.email]: true },
				}))
			);

			setLiveNotifications([]);
		} catch (error) {
			console.error(error);
			throw new Error('Error marking all notifications as read');
		}
	};

	const handleOpenChange = (next) => {
		const shouldMarkAsRead = openRef.current && !next;
		openRef.current = next;
		setOpen(next);

		if (shouldMarkAsRead) {
			void markAllAsRead().catch(console.error);
		}
	};

	const handleTriggerPointerDown = (event) => {
		if (!event.isPrimary || event.pointerType === 'mouse') return;

		triggerPointerActivatedRef.current = true;
		handleOpenChange(!openRef.current);
	};

	const handleTriggerClick = (event) => {
		if (!triggerPointerActivatedRef.current) return;

		triggerPointerActivatedRef.current = false;
		event.preventDefault();
	};

	const handleNotificationClick = async ({ id, event_id, lobby_id }) => {
		await markAsRead(id);

		const url =
			event_id && lobby_id
				? `${ROUTES.LOBBY_DETAILS}?id=${lobby_id}&eventId=${event_id}`
				: event_id
					? `${ROUTES.EVENT_DETAILS}?id=${event_id}`
					: null;

		if (url) {
			navigate(url);
		}
	};

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					onPointerDown={handleTriggerPointerDown}
					onClick={handleTriggerClick}
					aria-label={
						unreadCount > 0 ? 'Notifications, unread notifications available' : 'Notifications'
					}
					variant="ghost"
					size="icon"
					className={cn(
						'relative w-[48px] h-[48px] bg-[#1C2D4A] text-white p-2 rounded-full',
						triggerClassName
					)}
				>
					<Bell className="h-6 w-6" />
					{unreadCount > 0 && (
						<span
							className="absolute right-0 top-0 size-2.5 rounded-full bg-[#2667FF] ring-2 ring-white"
							aria-hidden="true"
						/>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[296px] p-0 bg-[#171E36] border border-[#2D3A6C] rounded-tl rounded-tr shadow-lg max-h-[70vh] flex flex-col"
				align="end"
				sideOffset={8}
			>
				<div className="px-3 py-1 border-b border-white/10 flex items-center justify-between">
					<h3 className="text-sm font-bold text-white">Notifications</h3>
				</div>

				<div className="overflow-y-auto flex-grow">
					{isLoading ? (
						<div className="flex items-center justify-center p-8">
							<div className="w-6 h-6 border-2 border-white/10 border-t-[#2667FF] rounded-full animate-spin" />
						</div>
					) : notificationData.length === 0 ? (
						<div className="p-8 text-center text-gray-400">
							<Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
							<p>No notifications</p>
						</div>
					) : (
						<AnimatePresence>
							{notificationData.map((notification) => {
								const isRead = notification.is_read && notification.is_read[user?.email];

								return (
									<motion.div
										key={notification.id}
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										exit={{ opacity: 0, height: 0 }}
										className={cn(
											'border-b border-white/5 last:border-0 cursor-pointer transition-colors',
											isRead
												? 'bg-[#101423] hover:bg-[#101423]/50'
												: 'bg-[#1E2748] hover:bg-[#1E2748]/50'
										)}
										onClick={() => handleNotificationClick(notification)}
									>
										<div className="p-2 flex gap-3">
											<div className="flex-1 min-w-0">
												<h4 className="font-normal text-sm text-[#949599]">{notification.title}</h4>
												<p
													className={cn(
														'text-sm font-normal line-clamp-3 break-words',
														isRead ? 'text-[#D4D5D8]' : 'text-[#F7F7F8]'
													)}
												>
													{notification.message}
												</p>
												<p className="text-[#81A1F8] text-xs font-medium">
													{getTimePassed(notification.created_date)}
												</p>
											</div>

											{!isRead && (
												<span
													className="self-start mt-1 mr-1 w-1.5 h-1.5 rounded-full bg-[#2667FF] shrink-0"
													aria-hidden
												/>
											)}
										</div>
									</motion.div>
								);
							})}
						</AnimatePresence>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
